import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { AppLayout, FormTabs } from './components/Layout';
import { RequireAuth } from './lib/auth';
import { SignIn } from './pages/SignIn';
import { ForgotPassword, ResetPassword, VerifyEmail } from './pages/PasswordReset';
import { SsoComplete } from './pages/SsoComplete';
import { FormList } from './pages/FormList';
import { Builder } from './pages/Builder';
import { Logic } from './pages/Logic';
import { Outcomes } from './pages/Outcomes';
import { Share } from './pages/Share';
import { Responses } from './pages/Responses';
import { Dashboard } from './pages/Dashboard';
import { Inbox } from './pages/Inbox';
import { AuditLog } from './pages/AuditLog';
import { Invites } from './pages/Invites';
import { Settings } from './pages/Settings';
import { Translations } from './pages/Translations';
import { Team } from './pages/Team';
import { Account } from './pages/Account';
import { Companies } from './pages/Companies';
import { Organisation } from './pages/Organisation';
import { Help } from './pages/Help';
import { Themes } from './pages/Themes';
import { ApiKeys } from './pages/ApiKeys';
import { AcceptInvite } from './pages/AcceptInvite';

/**
 * Routing uses react-router rather than the TanStack Router named in docs/16 §2.1.
 * A deliberate simplification for this slice; revisit if type-safe search params start
 * earning their setup cost (filters and segments are the likely trigger).
 */
export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<SignIn mode="login" />} />

      {/* Anonymous: the link arrives in a mail client, which is very often not the
          browser the recipient is signed in on. */}
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/register" element={<SignIn mode="register" />} />

      {/* Reached by people who cannot sign in, and by links in email, so all three are
          outside the authenticated shell. */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/sso/complete" element={<SsoComplete />} />

      <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
        <Route index element={<FormList />} />
        {/* Responses, without picking a form first. The response id is part of the path
            rather than component state so a row can be linked to and reopened. */}
        <Route path="responses" element={<Inbox />} />
        <Route path="responses/:responseId" element={<Inbox />} />
        <Route path="audit" element={<AuditLog />} />
        <Route path="team" element={<Team />} />
        <Route path="companies" element={<Companies />} />
        <Route path="organisation" element={<Organisation />} />
        <Route path="themes" element={<Themes />} />
        <Route path="api-keys" element={<ApiKeys />} />
        <Route path="account" element={<Account />} />
        {/* The topic is in the path so a page of the documentation can be linked to. */}
        <Route path="help" element={<Help />} />
        <Route path="help/:topic" element={<Help />} />
        <Route path="forms/:formId" element={<FormShell />}>
          <Route index element={<Builder />} />
          <Route path="logic" element={<Logic />} />
          <Route path="outcomes" element={<Outcomes />} />
          <Route path="share" element={<Share />} />
          <Route path="invites" element={<Invites />} />
          <Route path="translations" element={<Translations />} />
          <Route path="settings" element={<Settings />} />
          <Route path="responses" element={<Responses />} />
          <Route path="analytics" element={<Dashboard />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function FormShell() {
  return (
    <div className="flex h-full flex-col">
      <FormTabs />
      {/* Tabs stay mounted while the panel below them swaps. */}
      <div className="min-h-0 flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
